/**
 * SCENE 07: THE SEEN UNSEEN (Early Industrial Clockwork // 1850 AD)
 * Plunging inside gear mechanisms. Cursor illumination reveals star maps etched into hidden backs of gears.
 */

import { ISceneLifecycle } from './ISceneLifecycle';
import { CameraState } from '../renderer/CameraSpline';
import { EventBus, EventType } from '../core/EventBus';
import { StateStore, StateBitmask } from '../state/Store';

export class Scene07_Clockwork implements ISceneLifecycle {
  public id = 6;
  public name = 'The Seen Unseen';
  public era = 'Early Industrial Clockwork (1850 AD)';

  private eventBus: EventBus;
  private store: StateStore;
  private cursorLight: [number, number] = [0, 0];
  private isDiscovered: boolean = false;
  private discoveryProgress: number = 0;
  private unsubscribeList: Array<() => void> = [];

  constructor() {
    this.eventBus = EventBus.getInstance();
    this.store = StateStore.getInstance();
  }

  public async preload(): Promise<void> { return Promise.resolve(); }
  public mount(): void {}

  public activate(): void {
    const unsubMove = this.eventBus.subscribe(EventType.INPUT_POINTER_MOVE, (payload) => {
      this.cursorLight = [payload.x || 0, payload.y || 0];
      this.discoveryProgress = Math.min(1.0, this.discoveryProgress + 0.05);

      if (this.discoveryProgress >= 0.9 && !this.isDiscovered) {
        this.isDiscovered = true;
        this.store.setFlag(StateBitmask.CLOCKWORK_DISCOVERED, true);
        this.eventBus.emit(
          EventType.ACCESSIBILITY_ANNOUNCE,
          0, 0, 0, 0, 0, 1, 6,
          'Hidden star map discovered etched into the back of clockwork gears!'
        );
      }
    });

    this.unsubscribeList.push(unsubMove);
  }

  public update(_dt: number): void {}

  public render(ctx: CanvasRenderingContext2D, width: number, height: number, _camera: CameraState): void {
    ctx.save();
    const cx = width / 2;
    const cy = height / 2;

    // Dark Gear Chamber
    ctx.fillStyle = '#0a0907';
    ctx.fillRect(0, 0, width, height);

    // Large Brass Gear silhouette
    ctx.strokeStyle = '#3a3225';
    ctx.lineWidth = 14;
    ctx.beginPath();
    ctx.arc(cx, cy, 140, 0, Math.PI * 2);
    ctx.stroke();

    // Illuminating Light Spotlight
    const lx = ((this.cursorLight[0] + 1) / 2) * width;
    const ly = ((-this.cursorLight[1] + 1) / 2) * height;

    const spotlight = ctx.createRadialGradient(lx, ly, 10, lx, ly, 160);
    spotlight.addColorStop(0, 'rgba(240, 210, 150, 0.45)');
    spotlight.addColorStop(1, 'rgba(0,0,0,0.95)');

    ctx.fillStyle = spotlight;
    ctx.fillRect(0, 0, width, height);

    // Render Hidden Star Maps on back of gear when illuminated
    if (this.discoveryProgress > 0.1) {
      ctx.shadowColor = 'rgba(255, 230, 170, 0.9)';
      ctx.shadowBlur = 15;
      ctx.font = '600 12px "Inter", monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(255, 240, 210, 0.85)';
      ctx.fillText('CELESTIAL STAR MAP  //  THE UNSEEN CRAFT', cx, cy - 20);

      // Star constellation dots
      ctx.fillStyle = '#fff';
      for (let i = 0; i < 12; i++) {
        const sx = cx + Math.sin(i * 0.8) * 80;
        const sy = cy + Math.cos(i * 0.8) * 80;
        ctx.beginPath();
        ctx.arc(sx, sy, 3, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.font = '400 11px monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(200, 180, 150, 0.4)';
    ctx.fillText('[ EXPLORE GEAR SHAFT WITH CURSOR LIGHT ]', cx, height - 40);

    ctx.restore();
  }

  public deactivate(): void {
    this.unsubscribeList.forEach(fn => fn());
    this.unsubscribeList = [];
  }
  public unmount(): void {}
  public dispose(): void {}
}
